/**
 * @fileoverview Image component for rendering localized images with ImageStyle
 * @module screen-view-v2/components/shared/Image
 *
 * V2 adaptation:
 * - resolveAssetReference() instead of resolveAssetUrl()
 * - mapImageStyles() instead of mapImageBlockStyles()
 * - mapFrameLayoutStyles() instead of mapBoxBlockStyles()
 * - Size 'original' instead of 'origin'
 * - Only V2 scale mode values (no legacy Fit/Fill/Stretch)
 */

import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import type { LocalizedImage } from '@segmently/schema-models/v2';
import { resolveAssetReference, type AssetSize } from '../../../utils/contentResolver';
import { imageRenders } from '../../../utils/renderPredicates';
import { mapImageStyles, mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { useElementAnimation } from '../../../animations';
import { DEFAULT_PREVIEW_MODE, type PreviewMode } from '../../../types/previewMode';
import styles from './Image.module.css';

/**
 * V2 scale mode map — only V2 values, no legacy
 */
const SCALE_MODE_MAP: Record<string, CSSProperties['objectFit']> = {
  scaleToFill: 'fill',
  scaleAspectFit: 'contain',
  scaleAspectFill: 'cover',
  center: 'none',
  top: 'none',
  bottom: 'none',
  left: 'none',
  right: 'none',
};

/** G5 (R4): loading hints — passed only for article figures (ArticleFigure → Media). Absent → the <img> is unchanged. */
export interface ImageLoadingHints {
  loading?: 'eager' | 'lazy';
  decoding?: 'async' | 'sync' | 'auto';
  fetchPriority?: 'high' | 'low' | 'auto';
}

type ImageHintAttributes = {
  loading?: 'eager' | 'lazy';
  decoding?: 'async' | 'sync' | 'auto';
  fetchpriority?: 'high' | 'low' | 'auto';
};

/**
 * React 18.3 has no `fetchPriority` prop (it renders it but warns in development); the lower-case attribute renders as
 * is in SSR and on the client. No hints → `{}` → funnel <img> bytes unchanged.
 */
export function imageHintAttributes(hints: ImageLoadingHints | undefined): ImageHintAttributes {
  if (!hints) return {};
  return {
    ...(hints.loading ? { loading: hints.loading } : {}),
    ...(hints.decoding ? { decoding: hints.decoding } : {}),
    ...(hints.fetchPriority ? { fetchpriority: hints.fetchPriority } : {}),
  };
}

export interface ImageProps {
  image: LocalizedImage | undefined;
  locale: string;
  alt?: string;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  /** Media owns the featuredMedia animation ref — set to avoid double application */
  skipElementAnimation?: boolean;
  fillContainer?: boolean;
  style?: CSSProperties;
  scaleMode?: string;
  previewMode?: PreviewMode;
  onLoad?: () => void;
  onError?: () => void;
  cornerRadius?: number;
  /** G5 (R4): article figures only. */
  loadingHints?: ImageLoadingHints;
}

export const Image = memo(function Image({
  image,
  locale,
  alt = '',
  className,
  skipElementAnimation,
  'data-testid': testId,
  editPath,
  fillContainer = false,
  style,
  scaleMode: scaleModeOverride,
  previewMode,
  onLoad,
  onError,
  cornerRadius,
  loadingHints,
}: ImageProps): React.ReactElement | null {
  // Self-applied element animation (e.g. 'hero' on HeroContent); Media sets
  // skipElementAnimation because its wrapper already owns the featuredMedia ref
  const imageAnim = useElementAnimation(skipElementAnimation ? undefined : editPath);
  // V2: 'original' instead of 'origin'
  const mode = previewMode ?? DEFAULT_PREVIEW_MODE;
  const size: AssetSize = mode === 'static' ? 'small' : 'original';
  const src = image ? resolveAssetReference(image, locale, size) : null;

  const handleLoad = useCallback(() => {
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    onError?.();
  }, [onError]);

  // Renders nothing without a resolvable asset (shared with the entrance plan: utils/renderPredicates.ts)
  if (!imageRenders(image, locale) || src === null) {
    return null;
  }

  // V2: mapImageStyles replaces mapImageBlockStyles
  const imageStyles = mapImageStyles(image.appearance);

  // V2: mapFrameLayoutStyles replaces mapBoxBlockStyles
  const fullLayoutStyles: CSSProperties = mapFrameLayoutStyles(image.layout);
  const { borderRadius: _wrapperBorderRadius, ...layoutStylesWithDimensions } = fullLayoutStyles;
  const layoutStyles = fillContainer
    ? (({ width: _w, height: _h, ...rest }) => rest)(layoutStylesWithDimensions)
    : layoutStylesWithDimensions;

  // V2: appearance.scaleMode instead of styles.scaleMode
  const scaleMode = scaleModeOverride ?? image.appearance?.scaleMode;
  const objectFit = scaleMode ? SCALE_MODE_MAP[scaleMode] : undefined;

  const imgStyle: CSSProperties = fillContainer
    ? {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: objectFit ?? ('cover' as const),
        ...(imageStyles.borderRadius !== undefined && { borderRadius: imageStyles.borderRadius }),
        ...(cornerRadius !== undefined && { borderRadius: cornerRadius }),
      }
    : {
        display: 'block',
        ...imageStyles,
        ...(objectFit && { objectFit }),
        ...(cornerRadius !== undefined && { borderRadius: cornerRadius }),
      };

  const scaleModeClass = scaleMode ? styles[scaleMode as keyof typeof styles] : '';
  const schemaTestId = testId ?? getSchemaTestId(editPath);

  return (
    <div
      className={imageAnim.className ? `${styles.image} ${imageAnim.className}` : styles.image}
      style={{ ...layoutStyles, ...style, ...imageAnim.style }}
      data-anim={imageAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      <img
        src={src}
        alt={alt}
        className={`${className ?? ''} ${scaleModeClass}`.trim()}
        style={imgStyle}
        data-testid={schemaTestId ? `${schemaTestId}.img` : undefined}
        {...imageHintAttributes(loadingHints)}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});

Image.displayName = 'Image';
